import { useAtom } from "jotai";
import { useCallback } from "react";

import { aiDesignStateAtom } from "./state";

const MAX_HISTORY_ENTRIES = 10;

export type RefinementHistoryEntry = {
  imageId: number;
  imageSrc: string;
  prompt: string;
  timestamp: number;
};

type UseRefinementHistoryReturn = {
  addToHistory: (entry: Omit<RefinementHistoryEntry, "timestamp">) => void;
  undo: () => RefinementHistoryEntry | undefined;
  redo: () => RefinementHistoryEntry | undefined;
  canUndo: boolean;
  canRedo: boolean;
  clearHistory: () => void;
  currentEntry: RefinementHistoryEntry | undefined;
};

export function useRefinementHistory(side: "front" | "back"): UseRefinementHistoryReturn {
  const [aiDesignState, setAiDesignState] = useAtom(aiDesignStateAtom);
  const sideState = aiDesignState[side];
  const history = sideState.refinementHistory;
  const currentIndex = sideState.refinementHistoryIndex;

  const addToHistory = useCallback(
    (entry: Omit<RefinementHistoryEntry, "timestamp">) => {
      setAiDesignState(prev => {
        const prevSideState = prev[side];
        const prevHistory = prevSideState.refinementHistory;
        const prevIndex = prevSideState.refinementHistoryIndex;

        // When adding new entry, remove all entries after current index (redo stack)
        const newHistory = prevHistory.slice(0, prevIndex + 1);

        // Add new entry
        const newEntry: RefinementHistoryEntry = {
          ...entry,
          timestamp: Date.now()
        };
        newHistory.push(newEntry);

        // Keep only last 10 entries
        const trimmedHistory = newHistory.slice(-MAX_HISTORY_ENTRIES);

        return {
          ...prev,
          [side]: {
            ...prevSideState,
            refinementHistory: trimmedHistory,
            refinementHistoryIndex: trimmedHistory.length - 1
          }
        };
      });
    },
    [side, setAiDesignState]
  );

  const undo = useCallback((): RefinementHistoryEntry | undefined => {
    if (currentIndex > 0) {
      const newIndex = currentIndex - 1;
      setAiDesignState(prev => ({
        ...prev,
        [side]: {
          ...prev[side],
          refinementHistoryIndex: newIndex
        }
      }));
      return history[newIndex];
    }
    return undefined;
  }, [currentIndex, history, side, setAiDesignState]);

  const redo = useCallback((): RefinementHistoryEntry | undefined => {
    if (currentIndex < history.length - 1) {
      const newIndex = currentIndex + 1;
      setAiDesignState(prev => ({
        ...prev,
        [side]: {
          ...prev[side],
          refinementHistoryIndex: newIndex
        }
      }));
      return history[newIndex];
    }
    return undefined;
  }, [currentIndex, history, side, setAiDesignState]);

  const canUndo = currentIndex > 0;
  const canRedo = currentIndex < history.length - 1;

  const clearHistory = useCallback(() => {
    setAiDesignState(prev => ({
      ...prev,
      [side]: {
        ...prev[side],
        refinementHistory: [],
        refinementHistoryIndex: -1
      }
    }));
  }, [side, setAiDesignState]);

  const currentEntry = currentIndex >= 0 ? history[currentIndex] : undefined;

  return {
    addToHistory,
    undo,
    redo,
    canUndo,
    canRedo,
    clearHistory,
    currentEntry
  };
}
